<template>
  <div class="cf-confirm-container">
    <!-- ====== 顶部：筛选区域 ====== -->
    <div class="filter-section">
      <el-form :inline="true" size="small" class="filter-form" :model="filterForm">
        <el-form-item label="房屋地址">
          <el-input 
            v-model="filterForm.房屋地址" 
            placeholder="请输入房屋地址" 
            clearable 
            style="width:220px"
            @keyup.enter.native="handleSearch"
          />
        </el-form-item>

        <el-form-item label="业务状态">
          <el-select 
            v-model="filterForm.业务状态" 
            placeholder="请选择状态" 
            disabled
            style="width:140px"
          >
            <el-option label="待确认" value="待确认" />
          </el-select>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- ====== 中间：按钮区域 ====== -->
    <div class="button-section">
      <el-button 
        type="info" 
        size="medium" 
        icon="el-icon-view"
        :disabled="!canOperate"
        @click="handleDetail"
      >
        查看
      </el-button>
      <el-button 
        type="success" 
        size="medium" 
        icon="el-icon-check"
        :disabled="!canOperate || selectedRows.length !== 1"
        @click="handleApprove"
        :loading="approving"
      >
        通过
      </el-button>
      <el-button 
        type="danger" 
        size="medium" 
        icon="el-icon-close"
        :disabled="!canOperate || selectedRows.length !== 1"
        @click="handleReject"
        :loading="rejecting"
      >
        驳回
      </el-button>
    </div>

    <!-- ====== 下方：表格区域 ====== -->
    <div class="table-section">
      <el-table
        ref="tableRef"
        :data="tableData"
        border
        stripe
        highlight-current-row
        v-loading="loading"
        @selection-change="handleSelectionChange"
        @row-click="handleRowClick"
        @row-dblclick="handleRowDblclick"
        style="width: 100%"
        max-height="600"
      >
        <el-table-column type="selection" width="50" align="center" />
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column prop="行政区" label="行政区" width="100" align="center" show-overflow-tooltip />
        <el-table-column prop="房屋地址" label="房屋地址" min-width="180" align="center" show-overflow-tooltip />
        <el-table-column prop="户型" label="户型" width="80" align="center" />
        <el-table-column prop="出房租金（元/月）" label="出房租金（元/月）" width="140" align="center">
          <template slot-scope="{ row }">
            {{ row['出房租金（元/月）'] ? `¥${row['出房租金（元/月）']}` : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="出房押金（元）" label="出房押金（元/月）" width="140" align="center">
          <template slot-scope="{ row }">
            {{ row['出房押金（元）'] ? `¥${row['出房押金（元）']}` : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="成交差价（元/月）" label="成交差价（元/月）" width="150" align="center">
          <template slot-scope="{ row }">
            {{ row['成交差价（元/月）'] ? `¥${row['成交差价（元/月）']}` : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="出房费用（元）" label="出房费用（元）" width="130" align="center">
          <template slot-scope="{ row }">
            {{ row['出房费用（元）'] ? `¥${row['出房费用（元）']}` : '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="租金支付周期" label="租金支付周期" width="110" align="center" />
        <el-table-column prop="出房渠道" label="出房渠道" width="100" align="center" />
        <el-table-column prop="空置天数" label="空置天数" width="90" align="center" />
        <el-table-column prop="业务状态" label="业务状态" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="getBusinessStatusType(row.业务状态)" size="small">
              {{ row.业务状态 || '-' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="中止时间" label="中止时间" width="120" align="center">
          <template slot-scope="{ row }">
            {{ formatTimestamp(row.中止时间) }}
          </template>
        </el-table-column>
        <el-table-column prop="收房人" label="收房人" width="90" align="center" />
        <el-table-column prop="出房人" label="出房人" width="90" align="center" />
        <el-table-column prop="出房门店" label="出房门店" width="100" align="center" />
        <el-table-column prop="预定时间" label="预定时间" width="120" align="center">
          <template slot-scope="{ row }">
            {{ formatTimestamp(row.预定时间) }}
          </template>
        </el-table-column>
        <el-table-column prop="合同起始日" label="合同起始日" width="120" align="center">
          <template slot-scope="{ row }">
            {{ formatTimestamp(row.合同起始日) }}
          </template>
        </el-table-column>
        <el-table-column prop="合同到期日" label="合同到期日" width="120" align="center">
          <template slot-scope="{ row }">
            {{ formatTimestamp(row.合同到期日) }}
          </template>
        </el-table-column>
        <el-table-column prop="租客姓名" label="租客姓名" width="90" align="center" />
        <el-table-column prop="合同编号" label="合同编号" width="130" align="center" show-overflow-tooltip />
        <el-table-column prop="违约收入" label="违约收入" width="100" align="center">
          <template slot-scope="{ row }">
            {{ row.违约收入 ? `¥${row.违约收入}` : '-' }}
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-section">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pageInfo.pageIndex"
          :page-sizes="[10, 20, 30, 50]"
          :page-size="pageInfo.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pageInfo.total"
          background
        />
      </div>
    </div>

    <!-- ====== 详情弹窗（复用 CfHouse 的详情） ====== -->
    <el-dialog
      title="出房详情"
      :visible.sync="detailDialog.visible"
      width="1000px"
      :close-on-click-modal="false"
      class="detail-dialog"
    >
      <el-tabs v-model="detailActiveTab" type="border-card">
        <!-- 1. 基本信息 -->
        <el-tab-pane label="基本信息" name="base">
          <el-descriptions :column="2" border size="medium">
            <el-descriptions-item label="房屋地址" :span="2">
              {{ detailData.房屋地址 || '-' }}
            </el-descriptions-item>

            <el-descriptions-item label="租客姓名">
              {{ detailData.租客姓名 || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="联系电话">
              {{ detailData.联系电话 || '-' }}
            </el-descriptions-item>

            <el-descriptions-item label="出房人">
              {{ detailData.出房人 || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="出房门店">
              {{ detailData.出房门店 || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="合同编号">
              {{ detailData.合同编号 || '-' }}
            </el-descriptions-item>

            <el-descriptions-item label="开户行">
              {{ detailData.开户行 || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="银行卡号">
              {{ detailData.银行卡号 || '-' }}
            </el-descriptions-item>

            <el-descriptions-item label="成交周期（天）">
              {{ detailData['成交周期（天）'] || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="出房渠道">
              {{ detailData.出房渠道 || '-' }}
            </el-descriptions-item>

            <el-descriptions-item label="出房费用（元）">
              {{ detailData['出房费用（元）'] ? `¥${detailData['出房费用（元）']}` : '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="出房押金（元）">
              {{ detailData['出房押金（元）'] ? `¥${detailData['出房押金（元）']}` : '-' }}
            </el-descriptions-item>

            <el-descriptions-item label="合同起始日">
              {{ formatTimestamp(detailData.合同起始日) }}
            </el-descriptions-item>
            <el-descriptions-item label="合同到期日">
              {{ formatTimestamp(detailData.合同到期日) }}
            </el-descriptions-item>

            <el-descriptions-item label="出房租金不含物业/宽带费（元/月）" :span="2" :label-style="{ width: '220px' }">
              {{ detailData['出房租金（元/月）'] ? `¥${detailData['出房租金（元/月）']}` : '-' }}
            </el-descriptions-item>

            <el-descriptions-item label="租金支付周期">
              {{ detailData.租金支付周期 || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="定金金额">
              {{ detailData.定金金额 ? `¥${detailData.定金金额}` : '-' }}
            </el-descriptions-item>

            <el-descriptions-item label="物业/宽带费">
              {{ detailData.物业费 ? `¥${detailData.物业费}` : '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="支付周期">
              {{ detailData['物业/宽带费支付周期'] || '-' }}
            </el-descriptions-item>

            <el-descriptions-item label="水电气押金">
              {{ detailData.水电气押金 ? `¥${detailData.水电气押金}` : '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="宠物押金">
              {{ detailData.宠物押金 ? `¥${detailData.宠物押金}` : '-' }}
            </el-descriptions-item>

            <el-descriptions-item label="特别约定" :span="2">
              {{ detailData.特别约定 || '-' }}
            </el-descriptions-item>

            <el-descriptions-item label="是否更换出房人">
              {{ detailData.是否更换出房人 || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="是否更换收房人">
              {{ detailData.是否更换收房人 || '-' }}
            </el-descriptions-item>

            <el-descriptions-item label="紧急联系人号码">
              {{ detailData.紧急联系人号码 || '-' }}
            </el-descriptions-item>
            <el-descriptions-item label="计算空置天数">
              {{ detailData.计算空置天数 || '-' }}
            </el-descriptions-item>
          </el-descriptions>
        </el-tab-pane>

        <!-- 2. 相关附件 -->
        <el-tab-pane label="相关附件" name="attachment">
          <div class="attachment-section">
            <div class="file-group">
              <div class="file-group-title">身份证信息</div>
              <el-table :data="attachmentData['身份证信息']" border stripe size="small" v-loading="attachmentLoading">
                <el-table-column type="index" label="序号" width="60" align="center" />
                <el-table-column prop="show_name" label="文件名称" min-width="200" show-overflow-tooltip />
                <el-table-column label="预览" width="150" align="center">
                  <template slot-scope="{ row }">
                    <div v-if="isImage(row.suffix)" class="preview-thumbnail">
                      <el-image
                        :src="row.show_path_full || row.show_path"
                        fit="cover"
                        style="width: 80px; height: 60px; border-radius: 4px; cursor: pointer;"
                        :preview-src-list="[row.show_path_full || row.show_path]"
                      />
                    </div>
                    <span v-else class="preview-placeholder">仅支持图片预览</span>
                  </template>
                </el-table-column>
                <el-table-column prop="upload_time_clean" label="上传时间" width="170" align="center" />
                <el-table-column label="操作" width="160" align="center">
                  <template slot-scope="{ row }">
                    <el-button type="text" size="mini" @click="handleFileView(row)">查看</el-button>
                    <el-button type="text" size="mini" @click="handleFileDownload(row)">下载</el-button>
                  </template>
                </el-table-column>
              </el-table>
              <div v-if="!attachmentData['身份证信息'] || attachmentData['身份证信息'].length === 0" class="empty-tip">
                暂无身份证信息文件
              </div>
            </div>

            <div class="file-group">
              <div class="file-group-title">合同信息</div>
              <el-table :data="attachmentData['合同信息']" border stripe size="small" v-loading="attachmentLoading">
                <el-table-column type="index" label="序号" width="60" align="center" />
                <el-table-column prop="show_name" label="文件名称" min-width="200" show-overflow-tooltip />
                <el-table-column label="预览" width="150" align="center">
                  <template slot-scope="{ row }">
                    <div v-if="isImage(row.suffix)" class="preview-thumbnail">
                      <el-image
                        :src="row.show_path_full || row.show_path"
                        fit="cover"
                        style="width: 80px; height: 60px; border-radius: 4px; cursor: pointer;"
                        :preview-src-list="[row.show_path_full || row.show_path]"
                      />
                    </div>
                    <span v-else class="preview-placeholder">仅支持图片预览</span>
                  </template>
                </el-table-column>
                <el-table-column prop="upload_time_clean" label="上传时间" width="170" align="center" />
                <el-table-column label="操作" width="160" align="center">
                  <template slot-scope="{ row }">
                    <el-button type="text" size="mini" @click="handleFileView(row)">查看</el-button>
                    <el-button type="text" size="mini" @click="handleFileDownload(row)">下载</el-button>
                  </template>
                </el-table-column>
              </el-table>
              <div v-if="!attachmentData['合同信息'] || attachmentData['合同信息'].length === 0" class="empty-tip">
                暂无合同信息文件
              </div>
            </div>

            <div class="file-group">
              <div class="file-group-title">定金凭证</div>
              <el-table :data="attachmentData['出租预定凭证']" border stripe size="small" v-loading="attachmentLoading">
                <el-table-column type="index" label="序号" width="60" align="center" />
                <el-table-column prop="show_name" label="文件名称" min-width="200" show-overflow-tooltip />
                <el-table-column label="预览" width="150" align="center">
                  <template slot-scope="{ row }">
                    <div v-if="isImage(row.suffix)" class="preview-thumbnail">
                      <el-image
                        :src="row.show_path_full || row.show_path"
                        fit="cover"
                        style="width: 80px; height: 60px; border-radius: 4px; cursor: pointer;"
                        :preview-src-list="[row.show_path_full || row.show_path]"
                      />
                    </div>
                    <span v-else class="preview-placeholder">仅支持图片预览</span>
                  </template>
                </el-table-column>
                <el-table-column prop="upload_time_clean" label="上传时间" width="170" align="center" />
                <el-table-column label="操作" width="160" align="center">
                  <template slot-scope="{ row }">
                    <el-button type="text" size="mini" @click="handleFileView(row)">查看</el-button>
                    <el-button type="text" size="mini" @click="handleFileDownload(row)">下载</el-button>
                  </template>
                </el-table-column>
              </el-table>
              <div v-if="!attachmentData['出租预定凭证'] || attachmentData['出租预定凭证'].length === 0" class="empty-tip">
                暂无定金凭证文件
              </div>
            </div>
          </div>
        </el-tab-pane>

        <!-- 3. 付款计划 -->
        <el-tab-pane label="付款计划" name="payment">
          <div class="payment-section">
            <el-table :data="paymentList" border stripe style="width: 100%" v-loading="paymentLoading">
              <el-table-column prop="payment_date" label="付款时间" width="180" align="center">
                <template slot-scope="{ row }">
                  {{ row.payment_date || '-' }}
                </template>
              </el-table-column>
              <el-table-column prop="payment_amount" label="付款金额" width="150" align="center">
                <template slot-scope="{ row }">
                  ¥{{ (row.payment_amount || 0).toFixed(2) }}
                </template>
              </el-table-column>
              <el-table-column prop="status" label="状态" width="120" align="center">
                <template slot-scope="{ row }">
                  <el-tag :type="getPaymentStatusType(row.status)" size="small">
                    {{ getPaymentStatusLabel(row.status) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="第几次付款" label="第几次付款" width="120" align="center" />
            </el-table>
            <div v-if="paymentList.length === 0 && !paymentLoading" class="empty-payment">
              <i class="el-icon-document" />
              <span>暂无付款计划数据</span>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>

      <span slot="footer">
        <el-button @click="detailDialog.visible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { sqlBaseApi } from '@/api/通用/sqlBaseApi'
import { fileMethod } from '@/api/files/fileMethod'
import { sfPlanApi } from '@/api/付款/sfPlanApi'

export default {
  name: 'CfConfirm',

  data() {
    return {
      // ====== 筛选表单 ======
      filterForm: {
        房屋地址: '',
        业务状态: '待确认'  // 固定为待确认，不可修改
      },

      // ====== 表格数据 ======
      tableData: [],
      loading: false,
      selectedRows: [],

      // ====== 分页 ======
      pageInfo: {
        pageIndex: 1,
        pageSize: 10,
        total: 0
      },

      // ====== 按钮加载状态 ======
      approving: false,
      rejecting: false,

      // ====== 详情弹窗 ======
      detailDialog: {
        visible: false
      },
      detailData: {},
      detailActiveTab: 'base',

      // ====== 附件数据 ======
      attachmentData: {
        '身份证信息': [],
        '合同信息': [],
        '出租预定凭证': []
      },
      attachmentLoading: false,

      // ====== 付款计划 ======
      paymentList: [],
      paymentLoading: false
    }
  },

  computed: {
    canOperate() {
      return this.selectedRows.length === 1
    }
  },

  mounted() {
    this.init()
  },

  methods: {
    // ====== 初始化 ======
    init() {
      this.loadData()
    },

    // ====== 后端字段 -> 前端字段映射 ======
    mapFields(data) {
      if (!data) return {}

      const fieldMap = {
        'id': 'id',
        '房源ID': '房源ID',
        '行政区': '行政区',
        '房屋地址': '房屋地址',
        '户型': '户型',
        '出房租金（元/月）': '出房租金（元/月）',
        '出房押金（元）': '出房押金（元）',
        '成交差价（元/月）': '成交差价（元/月）',
        '出房费用（元）': '出房费用（元）',
        '租金支付周期': '租金支付周期',
        '出房渠道': '出房渠道',
        '空置天数': '空置天数',
        '业务状态': '业务状态',
        '中止时间': '中止时间',
        '收房人': '收房人',
        '出房人': '出房人',
        '出房门店': '出房门店',
        '预定时间': '预定时间',
        '合同起始日': '合同起始日',
        '合同到期日': '合同到期日',
        '租客姓名': '租客姓名',
        '合同编号': '合同编号',
        '违约收入': '违约收入',
        '联系电话': '联系电话',
        '开户行': '开户行',
        '银行卡号': '银行卡号',
        '成交周期（天）': '成交周期（天）',
        '定金金额': '定金金额',
        '物业费': '物业费',
        '物业/宽带费支付周期': '物业/宽带费支付周期',
        '水电气押金': '水电气押金',
        '宠物押金': '宠物押金',
        '特别约定': '特别约定',
        '是否更换出房人': '是否更换出房人',
        '是否更换收房人': '是否更换收房人',
        '紧急联系人号码': '紧急联系人号码',
        '计算空置天数': '计算空置天数'
      }

      const result = {}
      for (const [backendKey, frontendKey] of Object.entries(fieldMap)) {
        if (data.hasOwnProperty(backendKey)) {
          result[frontendKey] = data[backendKey]
        }
      }

      // 时间戳字段转为毫秒级
      const timestampFields = ['合同起始日', '合同到期日', '预定时间', '中止时间']
      timestampFields.forEach(field => {
        if (result[field] !== undefined && result[field] !== null && result[field] !== '') {
          const str = String(result[field])
          if (str.length === 10) {
            result[field] = result[field] * 1000
          }
        }
      })

      return result
    },

    // ====== 加载数据 ======
    loadData() {
      this.loading = true

      const params = {
        tableName: '出房管理',
        conditions: [],
        pageIndex: this.pageInfo.pageIndex,
        pageSize: this.pageInfo.pageSize
      }

      // 房屋地址 - 模糊匹配
      if (this.filterForm.房屋地址) {
        params.conditions.push({
          field: '房屋地址',
          type: 'STRING',
          operator: 'LIKE',
          value: `%${this.filterForm.房屋地址}%`
        })
      }

      // 业务状态 - 固定为"待确认"
      params.conditions.push({
        field: '业务状态',
        type: 'STRING',
        operator: 'EQ',
        value: '待确认'
      })

      // 只查询未删除的记录
      params.conditions.push({
        field: 'is_del',
        type: 'INTEGER',
        operator: 'EQ',
        value: 0
      })

      // 默认按创建时间倒序
      params.conditions.push({
        field: 'create_time',
        type: 'TIMESTAMP',
        operator: 'ORDER_BY_DESC'
      })

      console.log('请求参数===', params)

      sqlBaseApi.query(params).then((res) => {
        console.log('返回数据===', res)

        if (res.code === 200) {
          const rawList = res.data.list || []
          this.tableData = rawList.map(item => this.mapFields(item))
          this.pageInfo.total = res.data.total || 0
        } else {
          this.$message.error(res.message || '查询失败')
        }
      }).catch((err) => {
        console.error('请求失败===', err)
        this.$message.error('加载数据失败')
      }).finally(() => {
        this.loading = false
      })
    },

    // ====== 搜索 ======
    handleSearch() {
      this.pageInfo.pageIndex = 1
      this.loadData()
    },

    // ====== 重置搜索 ======
    resetSearch() {
      this.filterForm = {
        房屋地址: '',
        业务状态: '待确认'
      }
      this.handleSearch()
    },

    // ====== 分页 ======
    handleSizeChange(size) {
      this.pageInfo.pageSize = size
      this.loadData()
    },

    handleCurrentChange(page) {
      this.pageInfo.pageIndex = page
      this.loadData()
    },

    // ====== 表格选择变化 ======
    handleSelectionChange(selection) {
      console.log('选中行数:', selection.length)
      this.selectedRows = selection
    },

    // ====== 行点击 ======
    handleRowClick(row) {
      this.$refs.tableRef.toggleRowSelection(row)
    },

    // ====== 双击行 - 打开详情 ======
    handleRowDblclick(row) {
      this.detailData = { ...row }
      this.detailActiveTab = 'base'
      this.detailDialog.visible = true
      if (row.id) {
        this.loadAttachmentData(row.id)
        this.loadPaymentData(row.id)
      }
    },

    // ====== 查看详情 ======
    handleDetail() {
      if (this.selectedRows.length === 0) return
      this.detailData = { ...this.selectedRows[0] }
      this.detailActiveTab = 'base'
      this.detailDialog.visible = true
      if (this.selectedRows[0].id) {
        this.loadAttachmentData(this.selectedRows[0].id)
        this.loadPaymentData(this.selectedRows[0].id)
      }
    },

    // ====== 通过 ======
    handleApprove() {
      if (this.selectedRows.length !== 1) {
        this.$message.warning('请选择一条记录')
        return
      }

      const row = this.selectedRows[0]

      this.$confirm(
        '是否通过？通过后出现在出房管理中。',
        '通过确认',
        {
          type: 'warning',
          confirmButtonText: '确认通过',
          cancelButtonText: '取消'
        }
      ).then(() => {
        this.approving = true

        const params = {
          tableName: '出房管理',
          data: {
            业务状态: '已出租'
          },
          where: {
            id: row.id
          }
        }

        console.log('通过参数===', params)

        sqlBaseApi.update(params).then((res) => {
          console.log('通过返回===', res)

          if (res.code === 200) {
            this.$message.success('通过成功！')
            this.loadData()
            if (this.detailDialog.visible) {
              this.detailDialog.visible = false
            }
          } else {
            this.$message.error(res.message || '操作失败')
          }
        }).catch((err) => {
          console.error('通过请求失败===', err)
          this.$message.error(err.message || '操作失败，请稍后重试')
        }).finally(() => {
          this.approving = false
        })
      }).catch(() => {
        this.$message.info('已取消操作')
      })
    },

    // ====== 驳回 ======
    handleReject() {
      if (this.selectedRows.length !== 1) {
        this.$message.warning('请选择一条记录')
        return
      }

      const row = this.selectedRows[0]

      this.$confirm(
        '是否驳回？驳回后出现在待提交合同中。',
        '驳回确认',
        {
          type: 'warning',
          confirmButtonText: '确认驳回',
          cancelButtonText: '取消'
        }
      ).then(() => {
        this.rejecting = true

        const params = {
          tableName: '出房管理',
          data: {
            业务状态: '未提交'
          },
          where: {
            id: row.id
          }
        }

        console.log('驳回参数===', params)

        sqlBaseApi.update(params).then((res) => {
          console.log('驳回返回===', res)

          if (res.code === 200) {
            this.$message.success('驳回成功！')
            this.loadData()
            if (this.detailDialog.visible) {
              this.detailDialog.visible = false
            }
          } else {
            this.$message.error(res.message || '操作失败')
          }
        }).catch((err) => {
          console.error('驳回请求失败===', err)
          this.$message.error(err.message || '操作失败，请稍后重试')
        }).finally(() => {
          this.rejecting = false
        })
      }).catch(() => {
        this.$message.info('已取消操作')
      })
    },

    // ====== 业务状态标签颜色 ======
    getBusinessStatusType(status) {
      const map = {
        '待确认': 'info',
        '已出租': 'success',
        '未提交': 'warning'
      }
      return map[status] || 'info'
    },

    // ====== 付款状态映射 ======
    getPaymentStatusType(status) {
      const map = {
        'pending': 'danger',
        'paid': 'success',
        'partial': 'warning',
        'terminated': 'info',
        'other': 'info'
      }
      return map[status] || 'info'
    },

    getPaymentStatusLabel(status) {
      const map = {
        'pending': '待付',
        'paid': '已付',
        'partial': '部分付款',
        'terminated': '已中止',
        'other': '其他'
      }
      return map[status] || status
    },

    // ====== 时间戳格式化 ======
    formatTimestamp(timestamp) {
      if (!timestamp) return '-'
      let ts = timestamp
      const str = String(ts)
      if (str.length === 10) {
        ts = ts * 1000
      }
      const date = new Date(ts)
      if (isNaN(date.getTime())) {
        return '-'
      }
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    // ====== 刷新数据 ======
    refresh() {
      this.loadData()
    },

    // ====== 判断是否为图片 ======
    isImage(suffix) {
      if (!suffix) return false
      const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']
      return imageTypes.includes(suffix.toLowerCase())
    },

    // ====== 加载附件数据 ======
    async loadAttachmentData(fatherId) {
      if (!fatherId) return

      this.attachmentLoading = true
      const type1List = ['身份证信息', '合同信息', '出租预定凭证']
      const fileBaseUrl = window.applicationConfig?.fileBaseUrl || ''

      try {
        const promises = type1List.map(type1 => {
          return fileMethod.listFiles('出房管理', String(fatherId), type1, null)
            .then(res => {
              if (res.code === 200) {
                let data = res.data?.list || []
                data = data.map(item => {
                  if (item.show_path) {
                    const isFullUrl = item.show_path.startsWith('http://') || item.show_path.startsWith('https://')
                    item.show_path_full = isFullUrl ? item.show_path : fileBaseUrl + item.show_path.replace(/^\//, '')
                  } else {
                    item.show_path_full = ''
                  }
                  return item
                })
                return { type1, data }
              }
              return { type1, data: [] }
            })
            .catch(() => ({ type1, data: [] }))
        })

        const results = await Promise.all(promises)
        results.forEach(({ type1, data }) => {
          this.attachmentData[type1] = data
        })
      } catch (error) {
        console.error('加载附件失败:', error)
      } finally {
        this.attachmentLoading = false
      }
    },

    // ====== 查看文件 ======
    handleFileView(row) {
      if (row.show_path_full || row.show_path) {
        const url = row.show_path_full || row.show_path
        window.open(url, '_blank')
      } else {
        this.$message.warning('文件路径不存在')
      }
    },

    // ====== 下载文件 ======
    async handleFileDownload(row) {
      if (!row.show_path) {
        this.$message.warning('文件路径不存在')
        return
      }

      const loading = this.$loading({
        lock: true,
        text: '正在下载文件...',
        spinner: 'el-icon-loading',
        background: 'rgba(0, 0, 0, 0.7)'
      })

      try {
        const res = await fileMethod.downloadFile(row.show_path)
        const blob = new Blob([res.data])
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = row.show_name || '下载文件'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        this.$message.success('下载成功')
      } catch (error) {
        console.error('下载失败:', error)
        this.$message.error('下载失败，请重试')
      } finally {
        loading.close()
      }
    },

    // ====== 加载付款计划 ======
    async loadPaymentData(fatherId) {
      if (!fatherId) return

      this.paymentLoading = true

      try {
        const params = {
          房源ID: String(fatherId)
        }

        const res = await sfPlanApi.select(params)

        if (res.code === 200) {
          const list = res.data?.list || []
          this.paymentList = list.map(item => ({
            payment_date: this.formatTimestamp(item.付款日期),
            payment_amount: item.付款金额 || 0,
            status: this.mapPaymentStatus(item.状态),
            '第几次付款': item.第几次付款 || '-'
          }))
        } else {
          this.paymentList = []
        }
      } catch (error) {
        console.error('加载付款计划失败:', error)
        this.paymentList = []
      } finally {
        this.paymentLoading = false
      }
    },

    mapPaymentStatus(status) {
      const map = {
        '待付': 'pending',
        '部分付款': 'partial',
        '已完成': 'paid',
        '已中止': 'terminated',
        '已作废': 'other',
        '其他': 'other'
      }
      return map[status] || 'other'
    }
  }
}
</script>

<style scoped>
.cf-confirm-container {
  padding: 20px;
  background: #f0f2f5;
  min-height: 100vh;
}

.filter-section {
  background: #fff;
  padding: 20px 20px 0 20px;
  border-radius: 4px;
  margin-bottom: 16px;
}

.filter-form {
  margin-bottom: 0;
}

.button-section {
  background: #fff;
  padding: 12px 20px;
  border-radius: 4px;
  margin-bottom: 16px;
}

.button-section .el-button {
  margin-bottom: 4px;
}

.table-section {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
}

.pagination-section {
  margin-top: 20px;
  text-align: right;
}

.detail-dialog .el-tabs {
  margin-top: 0;
}

.detail-dialog .el-tabs__content {
  padding: 20px;
  max-height: 500px;
  overflow-y: auto;
}

.file-group {
  margin-bottom: 24px;
}

.file-group:last-child {
  margin-bottom: 0;
}

.file-group-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  padding: 8px 0;
  margin-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.preview-thumbnail {
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-placeholder {
  font-size: 12px;
  color: #909399;
}

.empty-tip {
  text-align: center;
  padding: 12px 0;
  color: #909399;
  font-size: 13px;
  background: #f5f7fa;
  border-radius: 4px;
  margin-top: 4px;
}

.payment-section {
  padding: 10px 0;
}

.empty-payment {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: #909399;
}

.empty-payment i {
  font-size: 48px;
  margin-bottom: 12px;
  color: #c0c4cc;
}

.empty-payment span {
  font-size: 14px;
}

@media (max-width: 768px) {
  .cf-confirm-container {
    padding: 10px;
  }

  .filter-form .el-form-item {
    display: block;
    margin-right: 0;
  }

  .button-section .el-button {
    margin-bottom: 8px;
  }
}
</style>